import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import loadGoogleMaps from '../../utils/mapsLoader';
import { validCoordinates } from '../../utils/geo';
import _useTranslate from '../../hooks/_useTranslate';
import './OrderTracking.css';

/* =====================================================================
   OrderTracking — "where is my order?"
   ---------------------------------------------------------------------
   Two independent halves, so the page is useful even when the second one
   has nothing to show (which is the COMMON case — most tenants' riders do
   not run the delivery app, and tracking is foreground-only):

     1. A status timeline, driven by the order's own status flow. Always
        available.
     2. A courier map, only when the tenant has opted into geo (IS_GEO +
        its own Maps key) AND a live position actually exists.

   Data comes from the `ordertracking` service, which returns a minimal
   projection for exactly this order — never a users document. It is
   POLLED: nothing about a courier's position is broadcast on a channel.

   HONESTY RULE: `lastGeolocation` updates stop when the rider locks his
   phone, so a marker can be minutes old. If the fix is older than
   STALE_AFTER_MS we show "last seen …" instead of implying it is live.
   ===================================================================== */

const POLL_MS = 15000;
const STALE_AFTER_MS = 2 * 60 * 1000;

// The kitchen flow (orders.js). 'Delivering' is when the rider is en route.
const STATUS_FLOW = ['Pending', 'Processing', 'Completed', 'Delivering', 'Delivered'];

const OrderTracking = ({ feathersStore }) => {

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Capability token for this order, handed over in the link that opened the
  // page (order history / post-payment). Without it the server will not
  // disclose a courier position for a guest or aggregator order.
  const trackingToken = searchParams.get('t') || '';
  const common = _useTranslate(feathersStore.language, 'common');

  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const mapRef = useRef(null);          // the DOM node
  const mapObjRef = useRef(null);       // google.maps.Map
  const markerRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; clearInterval(timerRef.current); }, []);

  const poll = useCallback(async () => {
    if (!id) return;
    try {
      const result = await feathersStore.getOrderTracking(id, trackingToken);
      if (!mountedRef.current) return;
      setTracking(result);
      setUnavailable(false);
      // Nothing more to watch once it is delivered — and stop the header
      // offering to track a finished order.
      if (result?.delivered) {
        clearInterval(timerRef.current);
        if (feathersStore.latestOrder?.id === id) feathersStore.clearLatestOrder();
      }
    } catch (error) {
      // The service may simply not be deployed yet, or the order may not be
      // ours. Either way this is "no tracking", not a crash.
      console.log('order tracking unavailable', error?.message);
      if (mountedRef.current) setUnavailable(true);
      clearInterval(timerRef.current);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id, trackingToken, feathersStore]);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [poll]);

  /* ---- the map half ---- */
  const position = tracking?.position;
  const showMap = feathersStore.canShowMap() && position && validCoordinates(position.latitude, position.longitude);

  useEffect(() => {
    if (!showMap || !mapRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps(feathersStore.tenantMapsKey());
        if (!maps || cancelled || !mapRef.current) return;
        const centre = { lat: position.latitude, lng: position.longitude };
        if (!mapObjRef.current) {
          mapObjRef.current = new maps.Map(mapRef.current, {
            center: centre,
            zoom: 15,
            disableDefaultUI: true,
            zoomControl: true
          });
        } else {
          mapObjRef.current.panTo(centre);
        }
        // A plain Marker (not AdvancedMarkerElement) so no Map ID is required.
        // `heading` is already published by the rider app and ignored by every
        // current consumer — free bearing rotation.
        const icon = {
          path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          rotation: Number.isFinite(+position.heading) ? +position.heading : 0,
          fillColor: '#1a73e8',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        };
        if (!markerRef.current) markerRef.current = new maps.Marker({ map: mapObjRef.current, position: centre, icon });
        else { markerRef.current.setPosition(centre); markerRef.current.setIcon(icon); }
      } catch (error) {
        console.log('tracking map failed to load', error?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [showMap, position?.latitude, position?.longitude, position?.heading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fixAgeMs = position?.updatedAt ? (Date.now() - new Date(position.updatedAt).getTime()) : null;
  const isStale = fixAgeMs === null || fixAgeMs > STALE_AFTER_MS;
  const lastSeen = position?.updatedAt
    ? new Date(position.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  const reachedIndex = (() => {
    const s = tracking?.status;
    const idx = STATUS_FLOW.indexOf(s);
    // The rider's own 'Delivered' wins over a lagging kitchen status.
    if (tracking?.delivered) return STATUS_FLOW.length - 1;
    return idx;
  })();

  if (loading) {
    return (
      <div className="tracking-container">
        <div className="tracking-loading"><CircularProgress color="secondary" size={'3rem'} /></div>
      </div>
    );
  }

  return (
    <div className="tracking-container">
      <div className="tracking-card">
        <h2 className="tracking-title">{common.orderStatus || 'Order status'}</h2>

        {unavailable ? (
          <div className="tracking-empty">
            {common.trackingUnavailable || 'Live tracking is not available for this order.'}
          </div>
        ) : (
          <>
            <ol className="tracking-steps">
              {STATUS_FLOW.map((step, index) => {
                const done = reachedIndex >= 0 && index <= reachedIndex;
                return (
                  <li key={step} className={`tracking-step${done ? ' tracking-step--done' : ''}`}>
                    {done ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    <span>{(common[`orderStatus${step}`]) || step}</span>
                  </li>
                );
              })}
            </ol>

            {showMap && (
              <>
                <div className="tracking-map" ref={mapRef} />
                <div className={`tracking-freshness${isStale ? ' tracking-freshness--stale' : ''}`}>
                  {isStale
                    ? `${common.courierLastSeen || 'Courier last seen'}${lastSeen ? ` ${lastSeen}` : ''}`
                    : (common.courierEnRoute || 'Courier on the way')}
                </div>
              </>
            )}

            {!showMap && !tracking?.delivered && tracking?.hasDriver && (
              <div className="tracking-note">
                {common.courierNoPosition || 'Your order is with a courier.'}
              </div>
            )}
          </>
        )}

        <Link className="tracking-home-link" to="/">{common.goToHome || 'Go to home'}</Link>
      </div>
    </div>
  );
};

export default inject('feathersStore')(observer(OrderTracking));
